import React, { useState, useEffect } from 'react';

import { CREATE_STREAM_STATUS_OPTIONS } from '@vlabs/api-bindings/src/luna-streams/constants';
import { selectGroupOptionsByAccountId } from '@vlabs/monokit/entities/sources/luna-streams/store/streams';
import validate from '@vlabs/shared/validators';
import { Control, Fold, SettingsItemWrapper } from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

function CommonSubFormComponent({
  getGroupOptionsByAccountId,
}) {
  const { t } = useTranslation();
  const [groupOptions, setGroupOptions] = useState([]);

  const { register, control, formState: { errors }, state, watch, setValue } = useFormContext();
  const statusValidationErrorMessage = t('lunaStreamsStreams:form.common.available_statuses', {
    statuses: CREATE_STREAM_STATUS_OPTIONS.raw.map(({ label }) => label).join(', '),
  });

  const accountId = watch('account_id');
  const selectedGroup = watch('group_name');

  useEffect(() => {
    const groupOptionsByAccountId = getGroupOptionsByAccountId(accountId);
    setGroupOptions(groupOptionsByAccountId);
    const selectedGroupIsAllowed = groupOptionsByAccountId
      .find((availableGroup) => selectedGroup?.value === availableGroup.value);
    if (selectedGroup && !selectedGroupIsAllowed) setValue('group_name', '');
  }, [accountId, getGroupOptionsByAccountId, setValue, selectedGroup]);

  return (
    <Fold isOpen title={t('lunaStreamsStreams:form.common.title')}>
      <SettingsItemWrapper title={t('lunaStreamsStreams:form.common.account_id')}>
        <Control.Input
          autoFocus
          errors={errors}
          {...register('account_id', {
            validate: validate.uuid(),
            required: validate.required(),
          })}
          disabled={state === 'edit'}
        />
      </SettingsItemWrapper>
      <SettingsItemWrapper title={t('lunaStreamsStreams:form.common.name')}>
        <Control.Input
          {...register('name', {
            maxLength: validate.maxLength(128),
          })}
          errors={errors}
        />
      </SettingsItemWrapper>
      <SettingsItemWrapper title={t('lunaStreamsStreams:form.common.description')}>
        <Control.Input
          {...register('description', {
            maxLength: validate.maxLength(512),
          })}
          errors={errors}
        />
      </SettingsItemWrapper>
      <SettingsItemWrapper title={t('lunaStreamsStreams:form.common.group')}>
        <Control.Select
          control={control}
          errors={errors}
          isClearable
          isCreatable
          name="group_name"
          options={groupOptions}
          rules={{
            validate: (group) => validate.regex(/^[a-zA-Z0-9_\\-]{1,128}$/)(group?.value),
          }}
        />
      </SettingsItemWrapper>
      <SettingsItemWrapper title={t('lunaStreamsStreams:form.common.status')}>
        <Control.Select
          control={control}
          errors={errors}
          name="status"
          options={CREATE_STREAM_STATUS_OPTIONS.raw}
          rules={{
            validate: {
              positive: (option) => CREATE_STREAM_STATUS_OPTIONS.raw
                .map(({ value }) => value)
                .includes(option?.value) || statusValidationErrorMessage,
            },
          }}
        />
      </SettingsItemWrapper>
    </Fold>
  );
}

CommonSubFormComponent.propTypes = {
  getGroupOptionsByAccountId: PropTypes.func,
};

CommonSubFormComponent.defaultProps = {
  getGroupOptionsByAccountId: undefined,
};

export const CommonSubForm = connect((state) => ({
  getGroupOptionsByAccountId: (accountId) => selectGroupOptionsByAccountId(state, accountId),
}))(CommonSubFormComponent);
